import {
  type Resolution,
  type ResolutionConfigItem,
  type ResolutionConfigItemType,
  RESOLUTION_PARAMETER_TYPES,
  REQS_REVIEW_RESOLUTION_TYPES,
} from '@frontend/api';
import {
  CircleEmptyIcon,
  UserIcon,
  WrenchIcon,
  CheckmarkIcon,
  MultipleWorkflowStatusIcon,
  ChatIcon,
  ListIcon,
} from '@frontend/components';

import { uuid } from 'utils/stringUtils';

export type ResolutionWithId = Resolution & {
  id: string;
};

export const addIdsToResolution = (
  resolution: Resolution,
): ResolutionWithId => ({
  ...resolution,
  id: uuid(),
});

export const stripIdsFromResolution = (
  resolution: ResolutionWithId,
): Resolution => {
  const { id, ...rest } = resolution;
  return rest;
};

export const createEmptyResolution = (
  resolutionType: ResolutionConfigItemType,
  config: ResolutionConfigItem,
): ResolutionWithId =>
  addIdsToResolution({
    type: resolutionType,
    customLabel:
      config.hasCustomLabel &&
      resolutionType === REQS_REVIEW_RESOLUTION_TYPES.RESOLVE
        ? 'Resolve this item'
        : undefined,
    parameters: config.requiredParameters.map((param) => ({
      name: param.name,
      value:
        param.parameterType === RESOLUTION_PARAMETER_TYPES.STRING
          ? ''
          : param.parameterType === RESOLUTION_PARAMETER_TYPES.BOOLEAN
          ? false
          : param.parameterType === RESOLUTION_PARAMETER_TYPES.NUMERIC_LIST
          ? []
          : param.parameterType === RESOLUTION_PARAMETER_TYPES.NUMERIC
          ? 0
          : '',
    })),
  });

export const getResolutionIcon = (
  resolutionType?: ResolutionConfigItemType,
) => {
  switch (resolutionType) {
    case REQS_REVIEW_RESOLUTION_TYPES.ADD_OPERATION:
      return WrenchIcon;
    case REQS_REVIEW_RESOLUTION_TYPES.NO_QUOTE:
      return CircleEmptyIcon;
    case REQS_REVIEW_RESOLUTION_TYPES.ASSIGN_ESTIMATOR:
      return UserIcon;
    case REQS_REVIEW_RESOLUTION_TYPES.SET_PROCESS:
      return ListIcon;
    case REQS_REVIEW_RESOLUTION_TYPES.UPDATE_WORKFLOW_STEP:
      return MultipleWorkflowStatusIcon;
    case REQS_REVIEW_RESOLUTION_TYPES.START_CHAT:
      return ChatIcon;
    case REQS_REVIEW_RESOLUTION_TYPES.RESOLVE:
      return CheckmarkIcon;
    default:
      return CheckmarkIcon;
  }
};

export const getResolutionDisplayString = (
  resolution: Pick<Resolution, 'type' | 'customLabel' | 'parameters'>,
  config: ResolutionConfigItem,
  isResolved = false,
) => {
  // If a custom label is provided, use it (with "Selected" prefix for resolved actions)
  if (config.hasCustomLabel) {
    if (isResolved) {
      // For resolved actions, add "Selected" prefix with quotes around custom label
      return `Selected "${resolution.customLabel}"`;
    }
    return resolution.customLabel;
  }

  const getParameter = (name: string) => {
    return resolution.parameters?.find((p) => p.name === name);
  };

  const getParameterValue = (name: string) => {
    const param = getParameter(name);
    if (!param) return '';
    if (Array.isArray(param?.value)) {
      return param?.value
        ?.map((v) => param?.displayValues?.[String(v)] ?? v)
        .join(', ');
    } else {
      return String(
        param.displayValues?.[String(param.value)] ?? param.value ?? '',
      );
    }
  };

  const formatStatus = (status: string) =>
    status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  switch (config.type) {
    case REQS_REVIEW_RESOLUTION_TYPES.ADD_OPERATION: {
      const param = getParameter('op_def_ids');
      if (!param) return isResolved ? 'Added operation' : 'Add operation';

      if (param.value.length > 1) {
        return isResolved
          ? `Added ${param.value.length} operations`
          : `Add ${param.value.length} operations`;
      } else {
        const opDefName = getParameterValue('op_def_ids');
        return isResolved
          ? `Added "${opDefName}" operation`
          : `Add "${opDefName}" operation`;
      }
    }
    case REQS_REVIEW_RESOLUTION_TYPES.ASSIGN_ESTIMATOR: {
      const estimatorName = getParameterValue('user_id');
      return isResolved
        ? `Assigned "${estimatorName}" as estimator`
        : `Assign "${estimatorName}" as estimator`;
    }
    case REQS_REVIEW_RESOLUTION_TYPES.SET_PROCESS: {
      const processId = getParameterValue('process_id');
      return isResolved
        ? `Set process to "${processId}"`
        : `Set process to "${processId}"`;
    }
    case REQS_REVIEW_RESOLUTION_TYPES.UPDATE_WORKFLOW_STEP: {
      const stepName = getParameterValue('step_locator');
      const statusValue = getParameterValue('status_value');
      if (statusValue) {
        const humanReadableStatus = formatStatus(statusValue);
        return isResolved
          ? `Set "${stepName}" step to "${humanReadableStatus}"`
          : `Set "${stepName}" step to "${humanReadableStatus}"`;
      }
      return isResolved
        ? `Updated workflow step "${stepName}"`
        : `Update workflow step "${stepName}"`;
    }
    case REQS_REVIEW_RESOLUTION_TYPES.START_CHAT:
      return isResolved ? 'Started chat' : 'Start chat';
    case REQS_REVIEW_RESOLUTION_TYPES.NO_QUOTE:
      return isResolved
        ? 'No quoted this line item'
        : 'No quote this line item';
    case REQS_REVIEW_RESOLUTION_TYPES.RESOLVE:
      return isResolved ? 'Resolved this item' : 'Resolve this item';
    default:
      return isResolved ? 'Dismissed' : 'Dismiss';
  }
};

export const getIsDestructive = (resolutionType: ResolutionConfigItemType) => {
  return resolutionType === REQS_REVIEW_RESOLUTION_TYPES.NO_QUOTE;
};

export const getResolutionKey = (
  resolution: Pick<Resolution, 'type' | 'customLabel' | 'parameters'>,
) => {
  return `${resolution.type}-${
    resolution.customLabel ?? ''
  }-${resolution.parameters
    ?.map((param) => `${param.name}-${String(param.value)}`)
    .join('-')}`;
};
